import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../consts";

interface ConnectivityState {
  /** Mirrors the last `networkStatusChange` we saw. Starts optimistic (`true`), as the signal did. */
  isConnectivityAvailable: boolean;
  /**
   * Tracks whether the "no connection" state has been announced. Module-private in the
   * signal version — it is written by the network listener and read by nothing else, so
   * keep it that way: do not add call sites outside `connectivityListener`.
   */
  isNoConnectionOpen: boolean;
  /**
   * Applies a `networkStatusChange` in one transition. The old signal code read
   * `isConnectivityAvailable.value` twice and then wrote it, so the read had to see the
   * *previous* value; folding it into a single action makes that ordering structural
   * rather than something a future edit can accidentally reverse. Returns which snack
   * (if any) the caller should raise, keeping the i18n/snack side effects out of the store.
   */
  applyNetworkStatus: (connected: boolean) => "online" | "offline" | undefined;
}

// Replaces the `isConnectivityAvailable` / `isNoConnectionOpen` preact signals.
//
// `create` is imported from "zustand" directly and NOT from feat/appState/resetState.
// That wrapper registers a store for `resetAllStores()` on logout; these signals were
// never reset on logout, and resetting `isConnectivityAvailable` back to `true` while
// actually offline would let requests through that the interceptor should cancel.
export const useConnectivityStore = create<ConnectivityState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        isConnectivityAvailable: true,
        isNoConnectionOpen: false,
        applyNetworkStatus: (connected) => {
          const wasAvailable = get().isConnectivityAvailable;

          if (connected && !wasAvailable) {
            set({ isNoConnectionOpen: false, isConnectivityAvailable: true }, false, "applyNetworkStatus/online");
            return "online";
          }
          if (!connected && wasAvailable) {
            set({ isNoConnectionOpen: true, isConnectivityAvailable: false }, false, "applyNetworkStatus/offline");
            return "offline";
          }

          set({ isConnectivityAvailable: connected }, false, "applyNetworkStatus");
          return undefined;
        },
      }),
      { enabled: DEVTOOLS_ENABLED, name: "connectivity" },
    ),
  ),
);

/// ˅˅˅ LEAF SELECTORS
//
// One hook per leaf field rather than a `useShallow` object: the signal subscribed
// per-signal, and a leaf selector keeps that same granularity under zustand's Object.is
// check. Reads *outside* a render (interceptors, store actions, event callbacks) must use
// `useConnectivityStore.getState()` instead — those never subscribed under signals either.

export const useIsConnectivityAvailable = () => useConnectivityStore((s) => s.isConnectivityAvailable);

/// ^^^ LEAF SELECTORS
